import React from 'react';

import { Step } from '@data/membershipForm';
import { makeMuiStyles, useCommonStyles } from '@styles';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexInline,
    flexDirection: 'row-reverse',
    overflow: 'hidden',
    [theme.breakpoints.down('xs')]: {
      '&$verticalInMobile': {
        ...common.flexColumnReverse,
      },
    },
    columnGap: 5,
    backgroundColor: theme.palette.background.paper,
    padding: 1,
    flexShrink: 0,
  },
  step: {
    ...common.flexCenter,
    ...common.middleText,
    height: 40,
    whiteSpace: 'pre',
    position: 'relative',
    '--bg': theme.palette.grey[400],
    backgroundColor: 'var(--bg)',
    color: theme.palette.common.white,
    flex: 1,
    '& + *': {
      marginRight: '5px',
    },
    '&$selected': {
      '--bg': theme.palette.primary.dark,
      color: theme.palette.common.white,
    },
    '&$horizontalInMobile + *': {
      marginRight: '2px',
    },
    '&:not(:last-child):before': {
      content: '""',
      position: 'absolute',
      height: '100%',
      left: -6,
      border: '20px solid var(--bg)',
      borderLeft: '6px solid transparent',
    },
    '&:not(:first-child):after': {
      content: '""',
      position: 'absolute',
      width: 6,
      height: '100%',
      top: 0,
      right: -26,
      border: '20px solid transparent',
      borderLeft: '6px solid var(--bg)',
    },
    '&$enabled': {
      ...common.baseButton,
    },
    [theme.breakpoints.down('xs')]: {
      '&$verticalInMobile:first-child': {
        height: 52,
        paddingTop: 10,
      },
      '&$verticalInMobile:last-child': {
        height: 30,
        paddingTop: 10,
      },
      '&$verticalInMobile': {
        flex: 'none',
        paddingTop: 20,
      },
      '&$verticalInMobile + *': {
        marginRight: 0,
      },
      '&:not(:last-child)$verticalInMobile:before': {
        width: '100%',
        height: 8,
        top: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        border: '400px solid transparent',
        borderTop: `14px solid ${theme.palette.common.white}`,
        borderBottomWidth: 0,
      },
      '&$verticalInMobile:after': {
        width: '100%',
        height: 12,
        top: 'auto',
        bottom: -12,
        left: '50%',
        transform: 'translateX(-50%)',
        border: '400px solid transparent',
        borderTop: '12px solid var(--bg)',
        borderBottomWidth: 0,
      },
    },
  },
  selected: {},
  enabled: {},
  verticalInMobile: {},
  horizontalInMobile: {},
}));

export interface StepperProps {
  steps: Step[];
  enabledSteps?: string[];
  value: string;
  baseUrl?: string;
}

export const Stepper: React.FC<StepperProps> = (props) => {
  const { steps, value, enabledSteps = [], baseUrl = '/utilities/MembershipForm/' } = props;

  const history = useHistory();

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  // If steps.length is more 5, In Mobile, It becomes vertical layout
  const isVerticalInMobile = steps.length >= 5;

  const rootClassName = clsx(
    classes.container,
    isVerticalInMobile ? classes.verticalInMobile : classes.horizontalInMobile,
  );

  const generateStepClassName = (stepName: string, enabled: boolean, asyncValue: string) => {
    return clsx(
      classes.step,
      stepName === asyncValue && classes.selected,
      enabled && classes.enabled,
      isVerticalInMobile ? classes.verticalInMobile : classes.horizontalInMobile,
    );
  };

  return (
    <div className={rootClassName}>
      {[...steps].reverse().map((step, i) => {
        const enabled = enabledSteps.includes(step.name);
        const stepClassName = generateStepClassName(step.name, enabled, value);

        return (
          <div
            key={i}
            className={stepClassName}
            onClick={enabled ? () => history.push(baseUrl + step.name) : undefined}
          >
            {step.label}
          </div>
        );
      })}
    </div>
  );
};
